/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-27
 */

// Add third-party dependencies.
import React from 'react';
import { Box, Paper, Typography, Button, Chip, useTheme } from '@mui/material';
import {
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import { useGetCurrentSubscriptionQuery } from '../../features/auth/authApi';

/**
 * Feature requirements for plan-based access control.
 */
export interface FeatureRequirement {
  /**
   * Minimum plan required for access.
   */
  minimumPlan: 'free' | 'starter' | 'growth' | 'pro';

  /**
   * Specific features required from plan.
   */
  requiredFeatures?: string[];

  /**
   * Custom check function for complex requirements.
   */
  customCheck?: (planData: any) => boolean;
}

/**
 * Props for the FeatureGate component.
 */
interface FeatureGateProps {
  /**
   * Feature requirements for access control.
   */
  requirement: FeatureRequirement;

  /**
   * Children to render if access is granted.
   */
  children: React.ReactNode;

  /**
   * Custom fallback component when access is denied.
   */
  fallback?: React.ReactNode;

  /**
   * Feature name for display in upgrade prompts.
   */
  featureName?: string;

  /**
   * Description of what upgrading unlocks.
   */
  upgradeDescription?: string;

  /**
   * Whether to show minimal blocking (just disable) vs full blocking (replace content).
   */
  minimal?: boolean;
}

/**
 * Plan hierarchy for upgrade suggestions.
 */
const PLAN_HIERARCHY = {
  free: 0,
  starter: 1,
  growth: 2,
  pro: 3,
};

/**
 * Plan display information.
 */
const PLAN_INFO = {
  starter: {
    name: 'Starter',
    price: '$27/month',
    color: '#1976d2',
  },
  growth: {
    name: 'Growth',
    price: '$37/month',
    color: '#2e7d32',
    badge: 'Most Popular',
  },
  pro: {
    name: 'Pro',
    price: '$67/month',
    color: '#ed6c02',
    badge: 'Premium',
  },
} as const;

/**
 * Check if user has access to a feature based on subscription and requirements.
 */
function checkFeatureAccess(
  subscription: any,
  requirement: FeatureRequirement
): boolean {
  // No subscription data means no access (except for free features)
  if (!subscription) {
    return requirement.minimumPlan === 'free';
  }

  const currentPlan = subscription.plan?.slug || 'free';
  const currentPlanLevel =
    PLAN_HIERARCHY[currentPlan as keyof typeof PLAN_HIERARCHY] ?? 0;
  const requiredPlanLevel = PLAN_HIERARCHY[requirement.minimumPlan];

  // Check plan level requirement
  if (currentPlanLevel < requiredPlanLevel) {
    return false;
  }

  // Check custom requirements
  if (requirement.customCheck) {
    return requirement.customCheck(subscription);
  }

  // Check specific features if required
  if (requirement.requiredFeatures && requirement.requiredFeatures.length > 0) {
    const userFeatures = subscription.plan?.features || [];
    return requirement.requiredFeatures.every(feature =>
      userFeatures.includes(feature)
    );
  }

  return true;
}

/**
 * Determine the target plan for upgrade based on current plan and requirement.
 */
function getUpgradeTargetPlan(
  currentPlan: string,
  minimumPlan: string
): string {
  const currentLevel =
    PLAN_HIERARCHY[currentPlan as keyof typeof PLAN_HIERARCHY] ?? 0;
  const minLevel = PLAN_HIERARCHY[minimumPlan as keyof typeof PLAN_HIERARCHY];

  // If already meeting minimum, suggest the next level up
  if (currentLevel >= minLevel) {
    const plans = Object.keys(PLAN_HIERARCHY);
    const nextPlanIndex = Math.min(currentLevel + 1, plans.length - 1);
    return plans[nextPlanIndex];
  }

  return minimumPlan;
}

/**
 * FeatureGate component that controls access to features based on subscription plan.
 * Provides upgrade prompts when access is denied.
 *
 * @param props - The component props
 * @returns {React.ReactElement | null} The feature gate component
 *
 * @example
 * <FeatureGate
 *   requirement={{ minimumPlan: 'growth' }}
 *   featureName="Advanced Analytics"
 * >
 *   <AdvancedAnalyticsComponent />
 * </FeatureGate>
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  requirement,
  children,
  fallback,
  featureName = 'This feature',
  upgradeDescription,
  minimal = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: subscription, isLoading } = useGetCurrentSubscriptionQuery();

  // Show loading state
  if (isLoading) {
    return minimal ? null : (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Checking access permissions...
        </Typography>
      </Box>
    );
  }

  // Check if user has access
  const hasAccess = checkFeatureAccess(subscription, requirement);

  // If user has access, render children
  if (hasAccess) {
    return children as React.ReactElement;
  }

  // If custom fallback provided, use it
  if (fallback) {
    return fallback as React.ReactElement;
  }

  // For minimal blocking, don't render anything
  if (minimal) {
    return null;
  }

  // Determine upgrade target plan
  const currentPlan = subscription?.plan?.slug || 'free';
  const targetPlan = getUpgradeTargetPlan(currentPlan, requirement.minimumPlan);
  const planInfo = PLAN_INFO[targetPlan as keyof typeof PLAN_INFO];

  // Handle upgrade navigation
  const handleUpgrade = () => {
    navigate('/pricing', {
      state: {
        highlightPlan: targetPlan,
        upgradeFrom: currentPlan,
        featureName,
      },
    });
  };

  // Render upgrade prompt
  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        textAlign: 'center',
        backgroundColor:
          theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <LockIcon
          sx={{
            fontSize: 48,
            color: 'text.secondary',
            mb: 1,
          }}
        />
      </Box>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {featureName} Requires Upgrade
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {upgradeDescription ||
          `${featureName} is available on the ${planInfo?.name || targetPlan} plan and higher.`}
      </Typography>

      {planInfo && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Chip
              label={planInfo.name}
              sx={{
                backgroundColor: planInfo.color,
                color: 'white',
                fontWeight: 600,
              }}
            />
            {'badge' in planInfo && planInfo.badge && (
              <Chip
                icon={<StarIcon sx={{ fontSize: 16 }} />}
                label={planInfo.badge}
                variant="outlined"
                size="small"
                sx={{ borderColor: planInfo.color, color: planInfo.color }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Starting at {planInfo.price}
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<UpgradeIcon />}
        onClick={handleUpgrade}
        sx={{
          px: 4,
          py: 1,
          fontWeight: 600,
        }}
      >
        Upgrade Plan
      </Button>
    </Paper>
  );
};

export default FeatureGate;
